import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Unsaved-work guard. Surfaces that buffer edits (the budget and drivers grids
 * type into local state and only POST on "Save") register themselves as dirty;
 * while anything is registered we intercept the ways out of the page:
 *
 *  - closing/reloading the tab → the browser's own beforeunload prompt
 *  - clicking any in-app link → confirm first (capture-phase, so it runs
 *    before react-router's own handler)
 *  - programmatic navigation → `useGuardedNavigate()`
 *
 * The browser back/forward buttons are NOT covered: blocking a POP needs a
 * react-router data router (createBrowserRouter + useBlocker) and this app
 * mounts a plain <BrowserRouter>.
 */

interface DirtyGuardApi {
  /** Labels of everything currently holding unsaved edits. */
  labels: string[];
  setDirty: (id: string, label: string | null) => void;
  /** True to proceed. Prompts only when something is actually dirty. */
  confirmLeave: () => boolean;
}

const DirtyGuardCtx = createContext<DirtyGuardApi | null>(null);

export function DirtyGuardProvider({ children }: { children: ReactNode }) {
  const entries = useRef(new Map<string, string>());
  const [labels, setLabels] = useState<string[]>([]);

  const setDirty = useCallback((id: string, label: string | null) => {
    if (label === null) {
      if (!entries.current.delete(id)) return;
    } else {
      if (entries.current.get(id) === label) return;
      entries.current.set(id, label);
    }
    setLabels(Array.from(entries.current.values()));
  }, []);

  const confirmLeave = useCallback(() => {
    const open = Array.from(entries.current.values());
    if (open.length === 0) return true;
    const what = open.length === 1 ? open[0] : `${open.length} places (${open.join(', ')})`;
    return window.confirm(`You have unsaved changes in ${what}. Leave and discard them?`);
  }, []);

  // Tab close / reload.
  useEffect(() => {
    if (labels.length === 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [labels.length]);

  // In-app link clicks. Capture phase so we get there before the router.
  useEffect(() => {
    if (labels.length === 0) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      let url: URL;
      try { url = new URL(anchor.href, window.location.href); } catch { return; }
      // Same-origin navigation away from this path only — in-page anchors and
      // external/download links are left alone.
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname) return;
      if (!confirmLeave()) { e.preventDefault(); e.stopPropagation(); }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [labels.length, confirmLeave]);

  const api = useMemo(() => ({ labels, setDirty, confirmLeave }), [labels, setDirty, confirmLeave]);
  return <DirtyGuardCtx.Provider value={api}>{children}</DirtyGuardCtx.Provider>;
}

/** No provider (tests, isolated renders) → a guard that never blocks. */
const NO_GUARD: DirtyGuardApi = { labels: [], setDirty: () => undefined, confirmLeave: () => true };

export function useDirtyGuard(): DirtyGuardApi {
  return useContext(DirtyGuardCtx) ?? NO_GUARD;
}

/**
 * Declares "this surface has unsaved edits" for as long as `isDirty` holds.
 * `label` is shown in the prompt, so phrase it as a place: "the budget grid".
 */
export function useDirtyFlag(isDirty: boolean, label: string): void {
  const id = useId();
  const { setDirty } = useDirtyGuard();
  useEffect(() => {
    setDirty(id, isDirty ? label : null);
  }, [id, isDirty, label, setDirty]);
  // Unmount (tab switch, route change) clears the flag.
  useEffect(() => () => setDirty(id, null), [id, setDirty]);
}

/** navigate() that asks first when something is unsaved. */
export function useGuardedNavigate(): (to: string) => void {
  const navigate = useNavigate();
  const { confirmLeave } = useDirtyGuard();
  return useCallback((to: string) => { if (confirmLeave()) navigate(to); }, [navigate, confirmLeave]);
}
